import { useQuery } from '@tanstack/react-query'
import { Copy, Headset, Mail, MessageCircle } from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'
import { API_PATHS } from '@/constants/api-paths'
import { api } from '@/lib/api'

type SupportContact = {
  name?: string
  email?: string
  /** Parent organisation's contact phone, reached over WhatsApp. */
  whatsapp?: string
}

function whatsappLink(number: string) {
  return `https://wa.me/${number.replace(/[^\d]/g, '')}`
}

async function copy(value: string) {
  try {
    await navigator.clipboard.writeText(value)
    toast.success(`Copied ${value}`)
  } catch {
    toast.error('Could not copy to clipboard')
  }
}

function ContactOption({
  icon: Icon,
  label,
  value,
  href,
  actionLabel,
}: {
  icon: typeof Mail
  label: string
  value: string
  href: string
  actionLabel: string
}) {
  return (
    <div className="flex items-center gap-3 rounded-xl border bg-card px-4 py-3">
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
        <Icon className="h-5 w-5" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="truncate text-sm font-semibold">{value}</p>
      </div>
      <Button variant="ghost" size="icon" onClick={() => copy(value)} title={`Copy ${label.toLowerCase()}`}>
        <Copy className="h-4 w-4" />
      </Button>
      <Button asChild size="sm">
        <a href={href} target="_blank" rel="noreferrer">
          {actionLabel}
        </a>
      </Button>
    </div>
  )
}

export function CustomerSupportDialog({
  open,
  onOpenChange,
  serviceInstanceId,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  serviceInstanceId?: string | null
}) {
  const org = String(serviceInstanceId ?? '').trim()
  const { data, isLoading } = useQuery({
    queryKey: ['customer-support-contact', org],
    queryFn: async () => {
      const { data } = await api.get<SupportContact>(API_PATHS.store.pos.supportContact, {
        params: org ? { org } : undefined,
      })
      return data
    },
    enabled: open,
    staleTime: 5 * 60 * 1000,
  })

  const email = (data?.email ?? '').trim()
  const whatsapp = (data?.whatsapp ?? '').trim()

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Headset className="h-6 w-6" />
          </div>
          <DialogTitle>Customer support</DialogTitle>
          <DialogDescription>
            {data?.name ? `${data.name} is here to help.` : "We're here to help."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {isLoading ? (
            <>
              <Skeleton className="h-16 w-full rounded-xl" />
              <Skeleton className="h-16 w-full rounded-xl" />
            </>
          ) : email || whatsapp ? (
            <>
              {email ? (
                <ContactOption
                  icon={Mail}
                  label="Email"
                  value={email}
                  href={`mailto:${email}`}
                  actionLabel="Email"
                />
              ) : null}
              {whatsapp ? (
                <ContactOption
                  icon={MessageCircle}
                  label="WhatsApp"
                  value={whatsapp}
                  href={whatsappLink(whatsapp)}
                  actionLabel="Chat"
                />
              ) : null}
            </>
          ) : (
            <p className="text-sm text-muted-foreground">
              Support contact details are not set up yet. Add a contact email and phone number to
              your parent organisation.
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
